import PropTypes from 'prop-types';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MainCard from 'components/MainCard';
import { brand } from 'utils/designTokens';

/**
 * Reusable summary cards component to display high-level report KPIs.
 */
export default function ReportSummaryCards({ cards }) {
  // If there are many cards, stack them cleanly
  const cardsCount = cards.length;
  let lgSize = 3; // default for 4 cards
  if (cardsCount === 1) lgSize = 12;
  else if (cardsCount === 2) lgSize = 6;
  else if (cardsCount === 3) lgSize = 4;
  else if (cardsCount === 5) lgSize = 2.4; // 12 / 5
  else if (cardsCount > 5) lgSize = 3;

  return (
    <Grid container spacing={2} sx={{ mb: 3.5 }}>
      {cards.map((card, index) => {
        const Icon = card.icon;
        return (
          <Grid 
            key={card.label || index} 
            size={{ xs: 12, sm: cardsCount > 1 ? 6 : 12, md: cardsCount > 2 ? 4 : 6, lg: lgSize }}
          >
            <MainCard
              sx={{
                height: '100%',
                position: 'relative',
                overflow: 'hidden',
                border: `1px solid ${brand.border}`,
                boxShadow: brand.shadowSm,
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: brand.shadowMd
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 3,
                  bgcolor: card.color || brand.primary
                }
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography
                      variant="overline"
                      color="text.secondary"
                      fontWeight={700}
                      sx={{ letterSpacing: '0.05em', display: 'inline-flex', alignItems: 'center' }}
                    >
                      {card.label}
                    </Typography>
                    {card.tooltip && (
                      <Tooltip title={card.tooltip} placement="top" arrow>
                        <InfoOutlinedIcon sx={{ fontSize: 13, color: 'text.disabled', cursor: 'help' }} />
                      </Tooltip>
                    )}
                  </Box>
                  <Typography 
                    variant="h3" 
                    fontWeight={800} 
                    sx={{ mt: 0.5, color: card.color || 'inherit', letterSpacing: '-0.02em' }}
                  >
                    {card.value}
                  </Typography>
                  {card.subtitle && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {card.subtitle}
                    </Typography>
                  )}
                </Box>
                {Icon && (
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      bgcolor: `${card.color || brand.primary}12`,
                      color: card.color || brand.primary,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                  >
                    <Icon sx={{ fontSize: 22 }} />
                  </Box>
                )}
              </Box>
            </MainCard>
          </Grid>
        );
      })}
    </Grid>
  );
}

ReportSummaryCards.propTypes = {
  cards: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      subtitle: PropTypes.string,
      tooltip: PropTypes.string,
      icon: PropTypes.elementType,
      color: PropTypes.string
    })
  ).isRequired
};
